import type { Cents } from "../types/money";
import { isValidIban } from "./supplier-invoice";

/**
 * SEPA-betaalbestand (pain.001.001.03) — de standaard waarmee je een stapel overboekingen in één
 * keer bij de bank aanlevert. Je uploadt het in Rabo Internetbankieren en tekent het daar met de
 * Rabo Scanner; deze code betaalt dus niets, hij schrijft alleen de opdracht.
 *
 * Bewust minimaal gehouden: één debiteur, één uitvoerdatum, SEPA-overboekingen in euro's. Dat is
 * precies wat een wekelijkse leveranciersrun nodig heeft, en elke bank accepteert dat profiel.
 */

export interface SepaBetaling {
  /** Uniek per betaling; komt terug op het afschrift en in de terugkoppeling van de bank. */
  id: string;
  bedragCents: Cents;
  iban: string;
  naam: string;
  /** Ongestructureerde omschrijving (factuurnummer) — max 140 tekens volgens de standaard. */
  omschrijving: string;
}

export interface SepaOpdracht {
  /** Onze eigen naam en rekening (de debiteur). */
  debiteurNaam: string;
  debiteurIban: string;
  debiteurBic?: string;
  /** Uitvoerdatum (epoch ms) — de dag waarop de bank moet overboeken. */
  uitvoerdatum: number;
  /** Vaste tijdstempel voor het bericht; meegeven houdt het bestand reproduceerbaar/testbaar. */
  aangemaaktOp: number;
  betalingen: SepaBetaling[];
}

export class SepaError extends Error {}

const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * SEPA laat alleen een beperkte tekenset toe. Accenten halen we van de letter af (é → e) in
 * plaats van het teken weg te gooien, anders wordt "Fáctuur" ineens "Fa ctuur"; lange streepjes
 * worden een gewoon koppelteken. Wat dan nog overblijft aan vreemde tekens wordt een spatie.
 */
function sepaTekst(s: string, max: number): string {
  return s
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[^A-Za-z0-9/\-?:().,'+ ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

const cents = (c: number): string => (c / 100).toFixed(2);
const datum = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const tijdstempel = (ms: number): string => new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");

/**
 * Bouwt het XML-bestand. Werpt bij alles wat een bank zou weigeren — liever hier stuklopen dan een
 * bestand dat de bank half accepteert.
 */
export function bouwSepaBestand(opdracht: SepaOpdracht): { xml: string; totaalCents: Cents; aantal: number } {
  const { betalingen } = opdracht;
  if (betalingen.length === 0) throw new SepaError("Geen betalingen in deze run.");
  if (!isValidIban(opdracht.debiteurIban)) throw new SepaError("Eigen IBAN klopt niet.");
  for (const b of betalingen) {
    if (!isValidIban(b.iban)) throw new SepaError(`IBAN van ${b.naam} klopt niet.`);
    if (!(b.bedragCents > 0)) throw new SepaError(`Bedrag voor ${b.naam} moet groter dan nul zijn.`);
  }
  const ids = new Set(betalingen.map((b) => b.id));
  if (ids.size !== betalingen.length) throw new SepaError("Dubbele betaal-id in de run.");

  const totaalCents = betalingen.reduce((n, b) => n + b.bedragCents, 0) as Cents;
  const berichtId = `GT-${datum(opdracht.aangemaaktOp).replace(/-/g, "")}-${betalingen.length}`;

  const transacties = betalingen
    .map(
      (b) => `      <CdtTrfTxInf>
        <PmtId><EndToEndId>${esc(sepaTekst(b.id, 35))}</EndToEndId></PmtId>
        <Amt><InstdAmt Ccy="EUR">${cents(b.bedragCents)}</InstdAmt></Amt>
        <Cdtr><Nm>${esc(sepaTekst(b.naam, 70))}</Nm></Cdtr>
        <CdtrAcct><Id><IBAN>${esc(b.iban.replace(/\s+/g, "").toUpperCase())}</IBAN></Id></CdtrAcct>
        <RmtInf><Ustrd>${esc(sepaTekst(b.omschrijving, 140))}</Ustrd></RmtInf>
      </CdtTrfTxInf>`,
    )
    .join("\n");

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.03">
  <CstmrCdtTrfInitn>
    <GrpHdr>
      <MsgId>${esc(berichtId)}</MsgId>
      <CreDtTm>${tijdstempel(opdracht.aangemaaktOp)}</CreDtTm>
      <NbOfTxs>${betalingen.length}</NbOfTxs>
      <CtrlSum>${cents(totaalCents)}</CtrlSum>
      <InitgPty><Nm>${esc(sepaTekst(opdracht.debiteurNaam, 70))}</Nm></InitgPty>
    </GrpHdr>
    <PmtInf>
      <PmtInfId>${esc(berichtId)}</PmtInfId>
      <PmtMtd>TRF</PmtMtd>
      <BtchBookg>false</BtchBookg>
      <NbOfTxs>${betalingen.length}</NbOfTxs>
      <CtrlSum>${cents(totaalCents)}</CtrlSum>
      <PmtTpInf><SvcLvl><Cd>SEPA</Cd></SvcLvl></PmtTpInf>
      <ReqdExctnDt>${datum(opdracht.uitvoerdatum)}</ReqdExctnDt>
      <Dbtr><Nm>${esc(sepaTekst(opdracht.debiteurNaam, 70))}</Nm></Dbtr>
      <DbtrAcct><Id><IBAN>${esc(opdracht.debiteurIban.replace(/\s+/g, "").toUpperCase())}</IBAN></Id></DbtrAcct>
      <DbtrAgt><FinInstnId>${opdracht.debiteurBic ? `<BIC>${esc(opdracht.debiteurBic)}</BIC>` : "<Othr><Id>NOTPROVIDED</Id></Othr>"}</FinInstnId></DbtrAgt>
      <ChrgBr>SLEV</ChrgBr>
${transacties}
    </PmtInf>
  </CstmrCdtTrfInitn>
</Document>
`;

  return { xml, totaalCents, aantal: betalingen.length };
}

/** Bestandsnaam voor de download: herkenbaar en sorteerbaar. */
export function sepaBestandsnaam(uitvoerdatum: number): string {
  return `betaalrun-${datum(uitvoerdatum)}.xml`;
}
